import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "./api.ts";
import type { Me } from "./session.ts";
import { Button, cx, Dialog } from "./ui.tsx";

/** Opens the tour again from anywhere (the account menu). */
export const replayTour = () => window.dispatchEvent(new Event("ninekeep:tour"));

const STEPS = [
  {
    title: "Your vendors owe you money",
    body: "SaaS vendors promise uptime. When they miss it, most owe you a service credit: money off your next bill. ninekeep finds every credit and gets it back for you.",
  },
  {
    title: "1. Drop in your contracts",
    body: "On Contracts, upload your vendor agreements, order forms and SLA addendums. ninekeep reads what each vendor promised, quoting the contract's own words, and you approve what it found.",
  },
  {
    title: "2. ninekeep keeps watch",
    body: "Once approved, ninekeep monitors each vendor through its status page and its own uptime checks, and works out every month the vendor fell short and what that's worth.",
  },
  {
    title: "3. You send the claim",
    body: "Before each deadline, ninekeep drafts the claim with its proof. You check it and send it, and ninekeep follows it until the credit lands on your invoice.",
  },
] as const;

/**
 * A short tour on a person's first sign-in: four steps, skippable, never offered again once
 * finished or skipped (on any device). The account menu's "Take the tour" opens it again.
 */
export function Tour({ me }: { me: Me }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(me.tour?.seen === false);
  const [step, setStep] = useState(0);
  useEffect(() => {
    const replay = () => {
      setStep(0);
      setOpen(true);
    };
    window.addEventListener("ninekeep:tour", replay);
    return () => window.removeEventListener("ninekeep:tour", replay);
  }, []);
  const finish = (to?: string) => {
    setOpen(false);
    if (me.tour?.seen === false) {
      qc.setQueryData<Me>(["me"], (m) => (m ? { ...m, tour: { seen: true } } : m));
      // Best effort: if it doesn't reach ninekeep, the tour simply shows once more next time.
      api.post("/auth/tour", {}).catch(() => {});
    }
    if (to) navigate(to);
  };
  const s = STEPS[step]!;
  const last = step === STEPS.length - 1;
  const first = me.user?.name?.split(/\s+/)[0];
  return (
    <Dialog
      open={open}
      title={first ? `Welcome to ninekeep, ${first}` : "Welcome to ninekeep"}
      onClose={() => finish()}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1.5" aria-hidden>
            {STEPS.map((x, i) => (
              <span
                key={x.title}
                className={cx("size-2 rounded-full", i === step ? "bg-ink" : "bg-line-strong")}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {last ? (
              <>
                <Button variant="ghost" onClick={() => finish()}>
                  Look around first
                </Button>
                <Button variant="primary" onClick={() => finish("/contracts")}>
                  Upload a contract
                </Button>
              </>
            ) : (
              <>
                {step === 0 ? (
                  <Button variant="ghost" onClick={() => finish()}>
                    Skip
                  </Button>
                ) : (
                  <Button variant="ghost" onClick={() => setStep(step - 1)}>
                    Back
                  </Button>
                )}
                <Button variant="primary" onClick={() => setStep(step + 1)}>
                  Next
                </Button>
              </>
            )}
          </div>
        </div>
      }
    >
      <p className="sr-only" aria-live="polite">
        Step {step + 1} of {STEPS.length}
      </p>
      <h3 className="font-display text-xl font-semibold tracking-tight">{s.title}</h3>
      <p className="text-muted">{s.body}</p>
      {last && (
        <p className="text-sm text-muted">
          You can take this tour again any time from your account menu, and Help explains each page.
        </p>
      )}
    </Dialog>
  );
}
